import React from "react";
import { Modal, Button } from "react-bootstrap";
import { Check, FileText, Info, Link as LinkIcon } from "lucide-react";
import "./SubmissionModal.css";
import { resolveMediaUrl } from "../../utils/media";

const formatDateTime = (value) => {
  try {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  } catch {
    return "-";
  }
};

const deriveFileName = (url) => {
  if (!url) return "";
  try {
    const noQuery = url.split("?")[0];
    const rawName = noQuery.split("/").pop() || "";
    return decodeURIComponent(rawName);
  } catch {
    return "";
  }
};

const SubmissionModal = ({ show, onHide, submission }) => {
  const submittedVia = submission?.submittedVia;
  const resolvedFileUrl = resolveMediaUrl(submission?.fileUrl);
  const resolvedLink = resolveMediaUrl(submission?.link);
  const fileName = submission?.fileName || deriveFileName(resolvedFileUrl);
  const hasLink = Boolean(resolvedLink);
  const hasFile = Boolean(resolvedFileUrl || fileName);
  const showLink = submittedVia === "link" ? hasLink : (!hasFile && hasLink);
  const showFile = submittedVia === "file" ? hasFile : (hasFile && !showLink);
  const timestamp = formatDateTime(submission?.submittedAt);
  const deadlineText = formatDateTime(submission?.deadline);
  const allowResubmission = Boolean(submission?.allowResubmission);

  const helperText = allowResubmission
    ? `Resubmissions are allowed${submission?.deadline ? ` until ${deadlineText}` : ""}. Your latest submission will be graded.`
    : "Resubmission is not allowed for this task.";

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" className="custom-submission-modal">
      <div className="modal-header-accent text-center pt-5 pb-3">
        <div className="success-icon-circle mx-auto mb-3">
          <Check size={28} className="text-white" />
        </div>
        <h3 className="text-white fw-bold m-0">Submission Successful!</h3>
      </div>

      <Modal.Body className="px-4 py-4">
        <div className="submission-file-card d-flex align-items-center mb-4">
          <div className="file-icon-box me-3">
            {showFile ? <FileText size={20} className="text-danger" /> : <LinkIcon size={20} className="text-primary" />}
          </div>
          <div className="flex-grow-1">
            {showFile ? (
              <>
                <h6 className="fw-bold m-0 file-name">{fileName || "Uploaded file"}</h6>
                {resolvedFileUrl ? (
                  <a href={resolvedFileUrl} target="_blank" rel="noreferrer" className="submission-file-link">
                    Open uploaded file
                  </a>
                ) : (
                  <small className="text-muted">File submission</small>
                )}
              </>
            ) : showLink ? (
              <>
                <h6 className="fw-bold m-0 file-name">Submitted Link</h6>
                <a href={resolvedLink || submission.link} target="_blank" rel="noreferrer" className="submission-link-value">
                  {resolvedLink || submission.link}
                </a>
              </>
            ) : (
              <>
                <h6 className="fw-bold m-0 file-name">Submission Received</h6>
                <small className="text-muted">No file or link details available.</small>
              </>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between text-muted small border-bottom pb-3 mb-3">
          <span>Timestamp:</span>
          <span className="fw-bold text-dark">{timestamp}</span>
        </div>

        <div className="info-alert d-flex align-items-start p-3 rounded-3 mb-4">
          <Info size={18} className="text-primary me-2 flex-shrink-0 mt-1" />
          <p className="small m-0 text-muted">{helperText}</p>
        </div>

        <Button variant="primary" className="w-100 rounded-pill py-2 fw-bold" onClick={onHide}>
          Back to Course
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export default SubmissionModal;
